import { MultiSelect, PriceBandIcon, SearchField, StatusBar } from '@weareopenr/component-library';
import { useMemo, useState } from 'react';
import { INestedPricingCategory } from 'src/api/getPricingCategories/types';
import { IPriceRule } from 'src/types/price.types';
import { type CategorySelectOption, pricingCategoriesToSelectOptions } from 'src/utils/pricingCategories';
import styles from './PricingCategoryStep.module.scss';

type PricingCategorySelectorStepProps = {
  categories: INestedPricingCategory[];
  selectedCategoryIds: string[];
  onChange: (ids: string[]) => void;
  rules?: IPriceRule[];
};

const filterCategories = (categories: INestedPricingCategory[], search: string): INestedPricingCategory[] => {
  if (!search) return categories;
  const lower = search.toLowerCase();
  return categories.reduce<INestedPricingCategory[]>((acc, cat) => {
    const nameMatches = cat.name.toLowerCase().includes(lower);
    const filteredChildren = filterCategories(cat.children, search);
    if (nameMatches || filteredChildren.length > 0) {
      acc.push({ ...cat, children: nameMatches ? cat.children : filteredChildren });
    }
    return acc;
  }, []);
};

const collectDisabledIds = (opts: CategorySelectOption[]): Set<string> =>
  opts.reduce<Set<string>>((ids, opt) => {
    if (opt.disabled) ids.add(opt.id);
    if (opt.children?.length) collectDisabledIds(opt.children).forEach((id) => ids.add(id));
    return ids;
  }, new Set());

export const PricingCategorySelectorStep = ({
  categories,
  selectedCategoryIds,
  onChange,
  rules,
}: PricingCategorySelectorStepProps) => {
  const [search, setSearch] = useState('');
  const options = pricingCategoriesToSelectOptions(filterCategories(categories, search), rules);
  const disabledIds = useMemo(() => collectDisabledIds(options), [options]);

  const handleChange = (ids: string[]) => onChange(ids.filter((id) => !disabledIds.has(id)));

  return (
    <section className={styles['category-section']}>
      <SearchField placeholder="Search for a category" onChange={(e) => setSearch(e.target.value)} isDisabled={false} />
      <MultiSelect
        className={styles['multi-select']}
        variant="list"
        Icon={PriceBandIcon}
        options={options}
        selected={selectedCategoryIds}
        onChange={handleChange}
        disableRecursiveParentSelection
      />
      <StatusBar
        className={styles['status-bar']}
        message={
          <span>
            <b>Creating a rule in a category that exists in a hierarchy:</b> Each product follows a single category
            path, and the rule from the deepest category in that path will apply for a given product.
          </span>
        }
        variant="info"
      />
    </section>
  );
};
